import { z } from "zod";

export const SCHEMA_VERSION = 1;

export const CellTypeSchema = z.enum(["markdown", "code", "latex", "mermaid", "text"]);

export const CellMetadataSchema = z
  .object({
    language: z.string().optional(),
    collapsed: z.boolean().optional(),
  })
  .catchall(z.unknown());

export const NotebookSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  sortOrder: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const CellSchema = z.object({
  id: z.string().min(1),
  noteId: z.string().min(1),
  type: CellTypeSchema,
  content: z.string(),
  sortOrder: z.number().int().nonnegative(),
  metadata: CellMetadataSchema.default({}),
});

export const NoteSchema = z.object({
  id: z.string().min(1),
  notebookId: z.string().min(1),
  title: z.string().min(1),
  tags: z.array(z.string()).default([]),
  favorite: z.boolean().default(false),
  trashedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  cellIds: z.array(z.string()).default([]),
});

export const VaultSettingsSchema = z.object({
  theme: z.enum(["system", "light", "dark"]).default("system"),
  splitPreview: z.boolean().default(true),
});

export const FletchyVaultSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  notebooks: z.array(NotebookSchema).default([]),
  notes: z.array(NoteSchema).default([]),
  cells: z.array(CellSchema).default([]),
  settings: VaultSettingsSchema.default({ theme: "system", splitPreview: true }),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const CountMapSchema = z.record(z.string(), z.number().int().nonnegative());

export const ImportResultSchema = z.object({
  importedCounts: CountMapSchema,
  skippedCounts: CountMapSchema,
  warnings: z.array(z.string()),
});

export type CellType = z.infer<typeof CellTypeSchema>;
export type CellMetadata = z.infer<typeof CellMetadataSchema>;
export type Notebook = z.infer<typeof NotebookSchema>;
export type Note = z.infer<typeof NoteSchema>;
export type Cell = z.infer<typeof CellSchema>;
export type FletchyVault = z.infer<typeof FletchyVaultSchema>;
export type ImportResult = z.infer<typeof ImportResultSchema>;
export type VaultData = string | ArrayBuffer | Uint8Array;

export interface StorageDriver {
  loadVault(): Promise<FletchyVault>;
  saveVault(vault: FletchyVault): Promise<void>;
  exportVault(): Promise<string>;
  importVault(data: VaultData): Promise<ImportResult>;
  resetVault(seed?: FletchyVault): Promise<FletchyVault>;
}
